/**
 * Combined Donut/Pill Chart Component
 * Outer ring: dimension with fewer unique values (donut)
 * Inner chart: dimension with more unique values (horizontal pill bar with segments)
 */

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { safeParseNumber } from '../utils/numberUtils';
import { jobChartVar, jobChartSeriesVars } from '../utils/job-detail-chart-theme';
import { ChartPopoverPortal } from './ChartPopoverPortal';
import { usePrefersFineHover } from '../utils/usePrefersFineHover';
import { JD_MORE_INFO_CHART_HIT } from '../../../../../utils/job-detail/job-detail-more-info-affordance';

interface DonutSunburstChartProps {
  outerData: Record<string, number>; // e.g., remoteness: { remote: 10, hybrid: 5, onsite: 3 }
  innerData: Record<string, number>; // e.g., locations: { Helsinki: 8, Espoo: 5, Tampere: 3, ... }
  outerTitle: string;
  innerTitle: string;
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
  maxInnerItems?: number;
  minOuterPercentage?: number; // Only show outer donut items >= this percentage (default: 3)
  outerCaption?: React.ReactNode; // Caption for outer donut
  innerCaption?: React.ReactNode; // Caption for inner pill
}

export const DonutSunburstChart: React.FC<DonutSunburstChartProps> = memo(({
  outerData,
  innerData,
  outerTitle,
  innerTitle,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
  maxInnerItems = 12,
  minOuterPercentage = 3,
  outerCaption,
  innerCaption,
}) => {
  type PopoverState = { kind: 'outer' | 'inner'; index: number; anchor: Element } | null;
  const [popover, setPopover] = useState<PopoverState>(null);
  const prefersFineHover = usePrefersFineHover();
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverCloseTimer = useCallback(() => {
    if (hoverCloseTimer.current !== null) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const scheduleCloseHover = useCallback(() => {
    clearHoverCloseTimer();
    hoverCloseTimer.current = setTimeout(() => {
      setPopover(null);
      hoverCloseTimer.current = null;
    }, 220);
  }, [clearHoverCloseTimer]);

  useEffect(() => () => clearHoverCloseTimer(), [clearHoverCloseTimer]);

  const openSegment = useCallback(
    (kind: 'outer' | 'inner', index: number, anchor: Element) => {
      clearHoverCloseTimer();
      setPopover({ kind, index, anchor });
    },
    [clearHoverCloseTimer],
  );

  const closePopover = useCallback(() => {
    clearHoverCloseTimer();
    setPopover(null);
  }, [clearHoverCloseTimer]);

  const toggleSegmentClick = useCallback((kind: 'outer' | 'inner', index: number, anchor: Element) => {
    setPopover((prev) =>
      prev && prev.kind === kind && prev.index === index ? null : { kind, index, anchor },
    );
  }, []);

  // Filter and validate outer data
  const allOuterEntries = Object.entries(outerData)
    .map(([label, value]) => [label, safeParseNumber(value)] as [string, number | null])
    .filter(([, value]) => value !== null && value > 0) as Array<[string, number]>;
  
  // Filter and validate inner data
  const innerEntries = Object.entries(innerData)
    .map(([label, value]) => [label, safeParseNumber(value)] as [string, number | null])
    .filter(([, value]) => value !== null && value > 0) as Array<[string, number]>;
  
  if (allOuterEntries.length === 0 || innerEntries.length === 0) {
    return null;
  }
  
  // Calculate total from ALL outer entries (before limiting)
  const outerTotal = allOuterEntries.reduce((sum, [, value]) => sum + value, 0);
  
  // Filter outer items to only show those >= minOuterPercentage of total
  const sortedOuterEntries = allOuterEntries
    .filter(([, value]) => (value / outerTotal) * 100 >= minOuterPercentage)
    .sort(([, a], [, b]) => b - a);
  
  const isOuterLimited = allOuterEntries.length > sortedOuterEntries.length;
  
  // Sort and limit inner items
  const sortedInnerEntries = innerEntries
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxInnerItems);
  
  // Calculate totals
  const innerTotal = sortedInnerEntries.reduce((sum, [, value]) => sum + value, 0);
  
  const width = 380;
  const height = 380;
  const padding = 44;
  const centerX = width / 2;
  const centerY = height / 2;

  const outerRadius = 128;
  const outerInnerRadius = 86;

  const pillBarHeight = 14;
  const pillBarWidth = (outerInnerRadius - 14) * 1.55;
  const pillX = centerX - pillBarWidth / 2;
  const pillY = centerY - pillBarHeight / 2;
  const centerCircleRadius = 14;
  const centerCircleY = pillY - centerCircleRadius - 8;

  const primaryColor =
    brandColour && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(brandColour.trim())
      ? brandColour.trim()
      : jobChartVar.cta;
  const colors = [primaryColor, ...jobChartSeriesVars.slice(1)];
  
  // Calculate outer donut segments (using limited entries for display, but percentages based on full total)
  let outerCurrentAngle = -Math.PI / 2; // Start at top
  const outerSegments = sortedOuterEntries.map(([label, value], index) => {
    const percentage = (value / outerTotal) * 100;
    const angle = (value / outerTotal) * (2 * Math.PI);
    
    const startAngle = outerCurrentAngle;
    const endAngle = outerCurrentAngle + angle;
    
    // Calculate arc path for donut segment
    const largeArcFlag = angle > Math.PI ? 1 : 0;
    
    const x1 = centerX + outerRadius * Math.cos(startAngle);
    const y1 = centerY + outerRadius * Math.sin(startAngle);
    const x2 = centerX + outerRadius * Math.cos(endAngle);
    const y2 = centerY + outerRadius * Math.sin(endAngle);
    
    const x3 = centerX + outerInnerRadius * Math.cos(startAngle);
    const y3 = centerY + outerInnerRadius * Math.sin(startAngle);
    const x4 = centerX + outerInnerRadius * Math.cos(endAngle);
    const y4 = centerY + outerInnerRadius * Math.sin(endAngle);
    
    // Create path for donut segment
    const path = [
      `M ${x1} ${y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x4} ${y4}`,
      `A ${outerInnerRadius} ${outerInnerRadius} 0 ${largeArcFlag} 0 ${x3} ${y3}`,
      'Z',
    ].join(' ');
    
    // Label position (middle of arc, outside the donut)
    const labelAngle = startAngle + angle / 2;
    const labelRadius = outerRadius + 18;
    const labelX = centerX + labelRadius * Math.cos(labelAngle);
    const labelY = centerY + labelRadius * Math.sin(labelAngle);
    
    // Line start position (on the outer edge of the donut)
    const lineStartX = centerX + outerRadius * Math.cos(labelAngle);
    const lineStartY = centerY + outerRadius * Math.sin(labelAngle);
    
    // Line end position (just before the label)
    const lineEndX = centerX + (outerRadius + 12) * Math.cos(labelAngle);
    const lineEndY = centerY + (outerRadius + 12) * Math.sin(labelAngle);
    
    // Percentage position (middle of arc, on the donut segment)
    const percentageRadius = (outerRadius + outerInnerRadius) / 2; // Middle of donut ring
    const percentageX = centerX + percentageRadius * Math.cos(labelAngle);
    const percentageY = centerY + percentageRadius * Math.sin(labelAngle);
    
    // Text anchor based on angle
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (labelAngle > -Math.PI / 2 && labelAngle < Math.PI / 2) {
      textAnchor = 'start';
    } else {
      textAnchor = 'end';
    }
    
    outerCurrentAngle += angle;
    
    return {
      label,
      value,
      percentage: percentage.toFixed(1),
      path,
      color: colors[index % colors.length],
      labelX,
      labelY,
      textAnchor,
      midAngle: labelAngle,
      percentageX,
      percentageY,
      lineStartX,
      lineStartY,
      lineEndX,
      lineEndY,
    };
  });
  
  // Calculate inner pill chart - single horizontal pill bar with segments
  // Calculate segment positions (stacked horizontally)
  let currentX = pillX;
  const innerSegments = sortedInnerEntries.map(([label, value], index) => {
    const percentage = (value / innerTotal) * 100;
    const segmentWidth = (value / innerTotal) * pillBarWidth;
    
    const segmentX = currentX;
    const segmentY = pillY;
    
    // Percentage label position - center of segment on the pill
    const percentageX = segmentX + segmentWidth / 2;
    const percentageY = pillY + pillBarHeight / 2;
    
    // Text label position - starts at the bottom of the pill, rotated 90 degrees to write downwards
    const labelX = segmentX + segmentWidth / 2;
    const labelY = pillY + pillBarHeight + 8; // Start just below the pill (room for larger label text)
    
    // Move to next segment
    currentX += segmentWidth;
    
    return {
      label,
      value,
      percentage: percentage.toFixed(1),
      segmentX,
      segmentY,
      segmentWidth,
      segmentHeight: pillBarHeight,
      color: colors[(index + sortedOuterEntries.length) % colors.length],
      percentageX,
      percentageY,
      labelX,
      labelY,
    };
  });
  
  const popoverSegment =
    popover?.kind === 'outer' ? outerSegments[popover.index] : popover ? innerSegments[popover.index] : undefined;
  const popoverTotal = popover?.kind === 'outer' ? outerTotal : innerTotal;

  return (
    <div className={`donut-sunburst-chart ${className} flex flex-col items-center relative`}>
      <p className="text-xs text-jc-muted w-full max-w-[380px] text-center mb-1 px-1">
        <FormattedMessage
          id="jobChart.segmentHint"
          defaultMessage="Tap or hover a segment for details."
          description="Hint for interactive donut and pill chart"
        />
      </p>
      {/* Outer caption above - overlapping SVG */}
      {outerCaption && (
        <div
          className="w-full mb-0 relative z-10 px-1"
          style={{ marginBottom: '-44px', marginTop: '0' }}
        >
          {outerCaption}
        </div>
      )}

      <div className="flex flex-col items-center justify-center w-full relative">
        <svg
          width={width}
          height={height}
          className="w-full max-w-[380px] mx-auto block"
          style={{ display: 'block', overflow: 'visible' }}
          viewBox={`-${padding} -${padding} ${width + padding * 2} ${height + padding * 2}`}
        >
          {/* Outer donut segments */}
          {outerSegments.map((segment, index) => (
            <g key={`outer-${index}`}>
              <path
                d={segment.path}
                fill={segment.color}
                opacity="0.88"
                stroke="var(--jc-surface)"
                strokeWidth="1.5"
                className={`cursor-pointer touch-manipulation outline-none focus-visible:opacity-100 ${JD_MORE_INFO_CHART_HIT}`}
                style={{ cursor: 'pointer' }}
                onClick={(e) => toggleSegmentClick('outer', index, e.currentTarget)}
                onMouseEnter={(e) => prefersFineHover && openSegment('outer', index, e.currentTarget)}
                onMouseLeave={() => prefersFineHover && scheduleCloseHover()}
              />
              {/* Connecting line from donut to label */}
              <line
                x1={segment.lineStartX}
                y1={segment.lineStartY}
                x2={segment.lineEndX}
                y2={segment.lineEndY}
                stroke="var(--jc-border)"
                strokeWidth="1"
                opacity="0.45"
              />
              {/* Percentage on donut segment */}
              <text
                x={segment.percentageX}
                y={segment.percentageY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-white font-semibold"
                fontSize="11"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
              >
                {segment.percentage}%
              </text>
              {/* Outer labels */}
              <text
                x={segment.labelX}
                y={segment.labelY}
                textAnchor={segment.textAnchor}
                dominantBaseline="middle"
                fill="var(--jc-ink-secondary)"
                fontSize="13"
                fontWeight={600}
              >
                {segment.label}
              </text>
            </g>
          ))}
          
          {/* Inner pill bar - single horizontal pill with segments */}
          <g>
            {/* Background pill */}
            <rect
              x={pillX}
              y={pillY}
              width={pillBarWidth}
              height={pillBarHeight}
              rx={pillBarHeight / 2}
              fill="var(--jc-elevated)"
            />
            {/* Segments */}
            {innerSegments.map((segment, index) => {
              const isFirst = index === 0;
              const isLast = index === innerSegments.length - 1;
              const radius = pillBarHeight / 2;
              
              // Create path for segment with rounded corners only on ends
              let path = '';
              if (isFirst && isLast) {
                // Only one segment - fully rounded pill
                path = `M ${segment.segmentX + radius} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + radius}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight - radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX + radius} ${segment.segmentY + segment.segmentHeight}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX} ${segment.segmentY + segment.segmentHeight - radius}
                        L ${segment.segmentX} ${segment.segmentY + radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + radius} ${segment.segmentY}
                        Z`;
              } else if (isFirst) {
                // First segment - rounded left side only
                path = `M ${segment.segmentX + radius} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX + radius} ${segment.segmentY + segment.segmentHeight}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX} ${segment.segmentY + segment.segmentHeight - radius}
                        L ${segment.segmentX} ${segment.segmentY + radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + radius} ${segment.segmentY}
                        Z`;
              } else if (isLast) {
                // Last segment - rounded right side only
                path = `M ${segment.segmentX} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + radius}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight - radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX} ${segment.segmentY + segment.segmentHeight}
                        Z`;
              } else {
                // Middle segments - straight rectangle
                path = `M ${segment.segmentX} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX} ${segment.segmentY + segment.segmentHeight}
                        Z`;
              }
              
              return (
                <g key={`inner-${index}`}>
                  {/* Segment */}
                  <path
                    d={path}
                    fill={segment.color}
                    opacity="0.9"
                    className={`cursor-pointer touch-manipulation outline-none ${JD_MORE_INFO_CHART_HIT}`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => toggleSegmentClick('inner', index, e.currentTarget)}
                    onMouseEnter={(e) => prefersFineHover && openSegment('inner', index, e.currentTarget)}
                    onMouseLeave={() => prefersFineHover && scheduleCloseHover()}
                  />
                  {/* Percentage on the pill - only show if segment is wide enough */}
                  {segment.segmentWidth > 34 && (
                    <text
                      x={segment.percentageX}
                      y={segment.percentageY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-white font-semibold"
                      fontSize="10"
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                    >
                      {segment.percentage}%
                    </text>
                  )}
                {/* Label below the pill - rotated diagonally to write downwards */}
                <text
                  x={segment.labelX}
                  y={segment.labelY}
                  textAnchor="start"
                  dominantBaseline="hanging"
                  fill="var(--jc-muted)"
                  fontSize="11"
                  fontWeight={500}
                  transform={`rotate(60 ${segment.labelX} ${segment.labelY})`}
                >
                  {segment.label}
                </text>
                </g>
              );
            })}
          </g>
          
          {/* Center circle - positioned above the pill */}
          <circle
            cx={centerX}
            cy={centerCircleY}
            r={centerCircleRadius}
            fill="var(--jc-surface)"
            stroke="var(--jc-border)"
            strokeWidth="1"
          />
          <text
            x={centerX}
            y={centerCircleY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--jc-ink-secondary)"
            fontSize="13"
            fontWeight={600}
          >
            {innerTotal}
          </text>
        </svg>
      </div>
      
      {/* Inner caption below - overlapping SVG */}
      {innerCaption && (
        <div
          className="w-full mt-0 relative z-10 px-1"
          style={{ marginTop: '-44px', marginBottom: '0' }}
        >
          {innerCaption}
        </div>
      )}
      
      {/* Disclaimer when outer data is limited */}
      {isOuterLimited && (
        <div className="w-full mt-2 text-center">
          <p className="text-sm text-jc-muted italic">
            <FormattedMessage
              id="jobDetailPage.showingItemsAboveThreshold"
              defaultMessage="Showing only items above {percentage}%"
              description="Message indicating chart is filtered to show only items above a certain percentage threshold, where {percentage} is the threshold value"
              values={{ percentage: minOuterPercentage }}
            />
          </p>
        </div>
      )}

      {popover && popoverSegment ? (
        <ChartPopoverPortal
          open
          anchorEl={popover.anchor}
          onClose={closePopover}
          showBackdrop={!prefersFineHover}
          width={280}
          title={popoverSegment.label}
          onPopoverMouseEnter={prefersFineHover ? clearHoverCloseTimer : undefined}
          onPopoverMouseLeave={prefersFineHover ? scheduleCloseHover : undefined}
        >
          <div className="space-y-1.5">
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.count" defaultMessage="Job postings" description="Label in chart popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{popoverSegment.value}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.share" defaultMessage="Share of chart" description="Label in chart popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{popoverSegment.percentage}%</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.totalInRing" defaultMessage="Total in this ring" description="Label in chart popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{popoverTotal}</span>
            </div>
            {popover.kind === 'outer' ? (
              <p className="text-xs text-jc-muted pt-1 border-t border-jc-border/80">
                {outerTitle}
              </p>
            ) : (
              <p className="text-xs text-jc-muted pt-1 border-t border-jc-border/80">
                {innerTitle}
              </p>
            )}
            {companyName ? (
              <p className="text-xs text-jc-muted">
                <FormattedMessage
                  id="jobChart.popover.companyContext"
                  defaultMessage="Based on {companyName} listings in our data."
                  description="Footnote for company chart popover"
                  values={{ companyName }}
                />
              </p>
            ) : null}
          </div>
        </ChartPopoverPortal>
      ) : null}
    </div>
  );
});

DonutSunburstChart.displayName = 'DonutSunburstChart';
